import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import RsvpButton from '../components/RsvpButton';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ClubRelation = {
  id: string;
  slug: string;
  name: string;
  location?: string | null;
  logo?: string | null;
  hero_image?: string | null;
};

type FollowedClubRow = {
  club_id: string;
  created_at: string;
  club: ClubRelation | ClubRelation[] | null;
};

type FollowedClub = {
  club_id: string;
  created_at: string;
  club: {
    id: string;
    slug: string;
    name: string;
    location: string | null;
    logo: string | null;
    hero_image: string | null;
  } | null;
};

type LedClubRow = {
  id: string;
  slug: string;
  name: string;
  location?: string | null;
  logo?: string | null;
  hero_image?: string | null;
  leader_id?: string | null;
};

type LedClub = {
  id: string;
  slug: string;
  name: string;
  location: string | null;
  logo: string | null;
  hero_image: string | null;
  leader_id: string | null;
};

type UpdateClubRelation = {
  id: string;
  name: string;
  logo?: string | null;
};

type ClubUpdateRow = {
  id: string;
  title: string;
  body: string;
  image_url: string | null;
  created_at: string;
  club: UpdateClubRelation | UpdateClubRelation[] | null;
};

type ClubUpdate = {
  id: string;
  title: string;
  body: string;
  image_url: string | null;
  created_at: string;
  club: {
    id: string;
    name: string;
    logo: string | null;
  } | null;
};

type AttendanceRow = {
  run_id: string;
  run_date: string;
  club_id: string;
};

type ClubSummary = {
  id: string;
  slug: string;
  name: string;
  logo?: string | null;
  hero_image?: string | null;
};

type UpcomingRun = {
  id: string;
  club_id: string;
  club: ClubSummary | null;
  title: string | null;
  start_time: string;
  location_address: string | null;
  distance: string | null;
  vibes: string[];
  runDate: string;
  instanceId: string | null;
  status: 'scheduled' | 'canceled' | 'moved' | 'special';
};

type RunRow = {
  id: string;
  club_id: string;
  title?: string | null;
  day_of_week: string;
  start_time: string;
  location_address?: string | null;
  distance?: string | null;
  vibes?: string[] | null;
  run_date?: string | null;
  club?: ClubSummary | ClubSummary[] | null;
};

type RunInstanceRow = {
  id: string;
  run_id: string;
  club_id: string;
  run_date: string;
  status: 'scheduled' | 'canceled' | 'moved' | 'special';
  title?: string | null;
  start_time_override?: string | null;
  location_address_override?: string | null;
  distance_override?: string | null;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RUN_MILESTONES = [
  { count: 1, label: 'First Run' },
  { count: 5, label: '5 Runs' },
  { count: 10, label: '10 Runs' },
  { count: 25, label: '25 Runs' },
  { count: 50, label: '50 Runs' },
];

const CLUB_MILESTONES = [
  { count: 1, label: '1 Club' },
  { count: 3, label: '3 Clubs' },
  { count: 5, label: '5 Clubs' },
];

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function firstLineAddress(address?: string | null) {
  if (!address) return null;
  const parts = address.split(',').map((p) => p.trim());
  const street = parts[0];
  const city = parts[1] ?? null;
  const zipMatch = address.match(/\b\d{5}(-\d{4})?\b/);
  const zip = zipMatch ? zipMatch[0] : null;
  if (!city) return street;
  if (zip) return `${street}, ${city}, ${zip}`;
  return `${street}, ${city}`;
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function normalizeClubRelation(
  value: ClubRelation | ClubRelation[] | null | undefined
): FollowedClub['club'] {
  if (!value) return null;
  const club = Array.isArray(value) ? value[0] : value;
  if (!club) return null;
  return {
    id: club.id,
    slug: club.slug,
    name: club.name,
    location: club.location ?? null,
    logo: club.logo ?? null,
    hero_image: club.hero_image ?? null,
  };
}

function normalizeLedClub(club: LedClubRow): LedClub {
  return {
    id: club.id,
    slug: club.slug,
    name: club.name,
    location: club.location ?? null,
    logo: club.logo ?? null,
    hero_image: club.hero_image ?? null,
    leader_id: club.leader_id ?? null,
  };
}

function normalizeUpdateClubRelation(
  value: UpdateClubRelation | UpdateClubRelation[] | null | undefined
): ClubUpdate['club'] {
  if (!value) return null;
  const club = Array.isArray(value) ? value[0] : value;
  if (!club) return null;
  return { id: club.id, name: club.name, logo: club.logo ?? null };
}

function normalizeRunClub(
  value: ClubSummary | ClubSummary[] | null | undefined
): ClubSummary | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

function getIsoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function getDayName(d: Date) {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(d);
}

function formatTime(time?: string | null) {
  if (!time) return 'Time TBD';
  const trimmed = time.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [rawHour, rawMinute] = trimmed.split(':');
  const hour = Number(rawHour);
  if (isNaN(hour)) return trimmed;
  const minute = rawMinute ?? '00';
  const period = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour % 12 || 12;
  return minute === '00' ? `${displayHour}${period}` : `${displayHour}:${minute}${period}`;
}

function formatAgendaDate(iso: string): string {
  const today = getIsoDate(new Date());
  const tomorrow = getIsoDate(new Date(Date.now() + 86400000));
  if (iso === today) return 'Today';
  if (iso === tomorrow) return 'Tomorrow';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso + 'T00:00:00'));
}

// ---------------------------------------------------------------------------
// Streak calculation
// ---------------------------------------------------------------------------

function getIsoWeek(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  const year = monday.getFullYear();
  const start = new Date(year, 0, 1);
  const weekNum = Math.ceil(
    ((monday.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7
  );
  return `${year}-W${String(weekNum).padStart(2, '0')}`;
}

function computeWeeklyStreak(rows: AttendanceRow[]): {
  currentStreakWeeks: number;
  last8Weeks: Array<{ isoWeek: string; hasRun: boolean; isCurrent: boolean }>;
} {
  const weeksWithRuns = new Set(rows.map((r) => getIsoWeek(r.run_date)));

  const today = new Date();
  const last8: Array<{ isoWeek: string; hasRun: boolean; isCurrent: boolean }> = [];
  for (let i = 7; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i * 7);
    const isoWeek = getIsoWeek(getIsoDate(d));
    last8.push({
      isoWeek,
      hasRun: weeksWithRuns.has(isoWeek),
      isCurrent: i === 0,
    });
  }

  // Count streak: walk back from most recent completed week (index 6)
  // If current week (index 7) has a run, include it in the streak
  let streak = 0;
  const currentIdx = 7;
  if (last8[currentIdx].hasRun) {
    streak = 1;
    let pointer = currentIdx - 1;
    while (pointer >= 0 && last8[pointer].hasRun) {
      streak++;
      pointer--;
    }
  } else {
    let pointer = currentIdx - 1;
    while (pointer >= 0 && last8[pointer].hasRun) {
      streak++;
      pointer--;
    }
  }

  return { currentStreakWeeks: streak, last8Weeks: last8 };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ClubCard({
  club,
  footer,
  leaderMode = false,
}: {
  club: {
    id: string;
    slug: string;
    name: string;
    location: string | null;
    logo: string | null;
    hero_image: string | null;
  };
  footer?: React.ReactNode;
  leaderMode?: boolean;
}) {
  return (
    <div className="group overflow-hidden rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] shadow-sm transition">
      <Link to={`/clubs/${club.slug}`} className="block">
        <div className="relative h-48 w-full overflow-hidden bg-neutral-100">
          {club.hero_image ? (
            <img
              src={club.hero_image}
              alt={club.name}
              className="h-full w-full object-cover object-[50%_30%] transition duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-neutral-100 text-sm text-black/45">
              No image yet
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
          {club.logo && (
            <div className="absolute left-4 top-4 h-12 w-12 overflow-hidden rounded-2xl border border-white/40 bg-white shadow-md">
              <img src={club.logo} alt={club.name} className="h-full w-full object-cover" />
            </div>
          )}
          {leaderMode && (
            <div className="absolute right-4 top-4 rounded-full border border-white/30 bg-white/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur-md">
              Leader
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="text-lg font-semibold tracking-tight text-black">{club.name}</h3>
          {club.location && (
            <p className="mt-1 text-sm text-black/55">{firstLineAddress(club.location)}</p>
          )}
        </div>
      </Link>
      {footer ? <div className="px-4 pb-4">{footer}</div> : null}
    </div>
  );
}

function LoggedOutEmptyState() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
      <div className="mx-auto max-w-5xl px-4 pt-16 sm:px-6">
        {/* Hero */}
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-black/40">
            My Clubs
          </p>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-black sm:text-5xl md:text-6xl">
            Your running story
            <br />
            starts here
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-black/55">
            Track your runs, build streaks, and stay connected with the clubs you love — all in one
            place.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/auth"
              className="rounded-xl bg-black px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
            >
              Join Free
            </Link>
            <Link
              to="/auth"
              className="rounded-xl border border-black/15 bg-white px-6 py-3 text-sm font-medium text-black transition hover:border-black/30"
            >
              Sign In
            </Link>
          </div>
          <p className="mt-4 text-xs text-black/35">Join thousands of runners across the city</p>
        </div>

        {/* Blurred preview */}
        <div className="mt-14 select-none" aria-hidden="true">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
            {/* Sidebar preview */}
            <div className="w-full space-y-4 opacity-40 blur-[3px] lg:w-64 lg:shrink-0">
              {/* Profile card preview */}
              <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-black/10" />
                  <div className="space-y-1.5">
                    <div className="h-3.5 w-24 rounded-full bg-black/15" />
                    <div className="h-2.5 w-32 rounded-full bg-black/8" />
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  <div className="flex-1 rounded-2xl border border-black/8 bg-white px-3 py-2 text-center">
                    <div className="mx-auto mb-1 h-4 w-6 rounded bg-black/15" />
                    <div className="mx-auto h-2 w-10 rounded bg-black/8" />
                  </div>
                  <div className="flex-1 rounded-2xl border border-black/8 bg-white px-3 py-2 text-center">
                    <div className="mx-auto mb-1 h-4 w-4 rounded bg-black/15" />
                    <div className="mx-auto h-2 w-10 rounded bg-black/8" />
                  </div>
                </div>
              </div>

              {/* Streak preview */}
              <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm">
                <div className="mb-3 h-3 w-20 rounded-full bg-black/15" />
                <div className="flex gap-1.5">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className={`h-6 w-6 rounded-full ${i < 5 ? 'bg-amber-400' : 'bg-black/10'}`}
                    />
                  ))}
                </div>
                <div className="mt-3 h-2.5 w-28 rounded-full bg-amber-300" />
              </div>

              {/* Milestones preview */}
              <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm">
                <div className="mb-3 h-3 w-24 rounded-full bg-black/15" />
                <div className="flex flex-wrap gap-2">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={`h-7 w-16 rounded-full border ${i < 2 ? 'border-amber-300 bg-[#fff7cc]' : 'border-black/10 bg-neutral-100'}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Main content preview */}
            <div className="min-w-0 flex-1 space-y-4 opacity-40 blur-[3px]">
              {/* Upcoming runs preview */}
              <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-6 shadow-sm">
                <div className="mb-4 h-4 w-32 rounded-full bg-black/15" />
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="mb-3 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-black/10" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-36 rounded-full bg-black/15" />
                      <div className="h-2.5 w-24 rounded-full bg-black/8" />
                    </div>
                    <div className="h-8 w-20 rounded-xl bg-black/10" />
                  </div>
                ))}
              </div>

              {/* Clubs preview */}
              <div className="grid grid-cols-2 gap-3">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="h-44 rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe]"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function ProfileCard({
  profile,
  user,
  totalRuns,
  totalClubs,
}: {
  profile: { display_name?: string | null; profile_image?: string | null } | null;
  user: { email?: string | null; user_metadata?: { avatar_url?: string | null } } | null;
  totalRuns: number;
  totalClubs: number;
}) {
  const avatar = profile?.profile_image || user?.user_metadata?.avatar_url || null;
  const displayName =
    profile?.display_name?.trim() ||
    user?.email?.split('@')[0] ||
    'Runner';
  const displayLetter = displayName.charAt(0).toUpperCase();

  return (
    <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm">
      <div className="flex items-center gap-3">
        {avatar ? (
          <img
            src={avatar}
            alt={displayName}
            className="h-12 w-12 rounded-full border border-black/10 object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black text-sm font-medium text-white">
            {displayLetter}
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate font-semibold tracking-tight text-black">{displayName}</p>
          <Link to="/profile" className="text-xs text-black/40 transition hover:text-black/70">
            Edit profile
          </Link>
        </div>
      </div>

      <div className="mt-4 flex gap-3">
        <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-black/8 bg-white py-3">
          <span className="text-xl font-semibold tracking-tight text-black">{totalRuns}</span>
          <span className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.1em] text-black/45">
            Runs
          </span>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-black/8 bg-white py-3">
          <span className="text-xl font-semibold tracking-tight text-black">{totalClubs}</span>
          <span className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.1em] text-black/45">
            Clubs
          </span>
        </div>
      </div>
    </div>
  );
}

function StreakWidget({
  last8Weeks,
  currentStreakWeeks,
}: {
  last8Weeks: Array<{ isoWeek: string; hasRun: boolean; isCurrent: boolean }>;
  currentStreakWeeks: number;
}) {
  return (
    <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black/45">
        Weekly Streak
      </p>

      <div className="mt-3 flex items-center gap-1.5">
        {last8Weeks.map((week) => {
          let dotClass = '';
          if (week.hasRun) {
            dotClass = 'bg-amber-400';
          } else if (week.isCurrent) {
            dotClass = 'border-2 border-amber-400 bg-transparent';
          } else {
            dotClass = 'bg-black/10';
          }
          return (
            <div
              key={week.isoWeek}
              className={`h-6 w-6 rounded-full ${dotClass}`}
              title={week.isoWeek}
            />
          );
        })}
      </div>

      <p className="mt-3 text-sm font-medium text-black/70">
        {currentStreakWeeks > 0 ? (
          <span>
            <span className="font-semibold text-amber-500">{currentStreakWeeks}</span>
            {currentStreakWeeks === 1 ? ' week streak' : ' week streak'} — keep it going!
          </span>
        ) : (
          <span className="text-black/45">Start your streak this week!</span>
        )}
      </p>

      <p className="mt-1 text-[10px] text-black/30">Last 8 weeks · amber = run confirmed</p>
    </div>
  );
}

function MilestonesPanel({
  totalRuns,
  totalClubs,
}: {
  totalRuns: number;
  totalClubs: number;
}) {
  const nextRunMilestone = RUN_MILESTONES.find((m) => m.count > totalRuns);
  const nextClubMilestone = CLUB_MILESTONES.find((m) => m.count > totalClubs);

  return (
    <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black/45">
        Milestones
      </p>

      <div className="mt-3 space-y-3">
        <div>
          <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-black/35">
            Runs
          </p>
          <div className="flex flex-wrap gap-1.5">
            {RUN_MILESTONES.map((m) => {
              const earned = totalRuns >= m.count;
              return (
                <div
                  key={m.count}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                    earned
                      ? 'border-amber-300 bg-[#fff7cc] text-amber-900'
                      : 'border-black/10 bg-neutral-100 text-black/30'
                  }`}
                  title={earned ? `Earned: ${m.label}` : `${m.count - totalRuns} more to unlock`}
                >
                  {m.label}
                </div>
              );
            })}
          </div>
          {nextRunMilestone && (
            <p className="mt-2 text-[10px] text-black/35">
              {nextRunMilestone.count - totalRuns} more run
              {nextRunMilestone.count - totalRuns !== 1 ? 's' : ''} to unlock{' '}
              <span className="font-medium">{nextRunMilestone.label}</span>
            </p>
          )}
        </div>

        <div>
          <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-black/35">
            Clubs
          </p>
          <div className="flex flex-wrap gap-1.5">
            {CLUB_MILESTONES.map((m) => {
              const earned = totalClubs >= m.count;
              return (
                <div
                  key={m.count}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                    earned
                      ? 'border-amber-300 bg-[#fff7cc] text-amber-900'
                      : 'border-black/10 bg-neutral-100 text-black/30'
                  }`}
                  title={earned ? `Earned: ${m.label}` : `${m.count - totalClubs} more to unlock`}
                >
                  {m.label}
                </div>
              );
            })}
          </div>
          {nextClubMilestone && (
            <p className="mt-2 text-[10px] text-black/35">
              {nextClubMilestone.count - totalClubs} more club
              {nextClubMilestone.count - totalClubs !== 1 ? 's' : ''} to unlock{' '}
              <span className="font-medium">{nextClubMilestone.label}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function UpcomingRunsSection({
  upcomingRuns,
  userRsvps,
  loading,
}: {
  upcomingRuns: UpcomingRun[];
  userRsvps: Map<string, 'going' | 'interested'>;
  loading: boolean;
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, UpcomingRun[]>();
    for (const run of upcomingRuns) {
      const existing = map.get(run.runDate) ?? [];
      existing.push(run);
      map.set(run.runDate, existing);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [upcomingRuns]);

  return (
    <section className="rounded-[2rem] border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-6 shadow-sm md:p-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-black">Upcoming Runs</h2>
          <p className="mt-1 text-sm text-black/55">Next 7 days from your clubs.</p>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 space-y-3">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-2xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe]"
            />
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-black/10 bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] px-5 py-8 text-sm text-black/55">
          No runs in the next 7 days from your clubs.
          <div className="mt-4">
            <Link
              to="/explore"
              className="inline-flex rounded-2xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
            >
              Explore Clubs
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {grouped.map(([date, runs]) => (
            <div key={date}>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-black/45">
                {formatAgendaDate(date)}
              </p>
              <div className="space-y-2">
                {runs.map((run) => (
                  <div
                    key={`${run.id}-${run.runDate}`}
                    className="flex items-center gap-3 rounded-2xl border border-black/8 bg-white px-4 py-3"
                  >
                    {run.club?.logo ? (
                      <img
                        src={run.club.logo}
                        alt={run.club.name ?? ''}
                        className="h-9 w-9 shrink-0 rounded-xl border border-black/10 object-cover"
                      />
                    ) : (
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-black/5 text-xs font-semibold text-black/40">
                        {run.club?.name?.charAt(0) ?? '?'}
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-black">
                        {run.title || run.club?.name || 'Run'}
                      </p>
                      <p className="truncate text-xs text-black/45">
                        {formatTime(run.start_time)}
                        {run.location_address
                          ? ` · ${firstLineAddress(run.location_address)}`
                          : ''}
                        {run.distance ? ` · ${run.distance}` : ''}
                      </p>
                    </div>

                    {run.status === 'canceled' ? (
                      <span className="shrink-0 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-[11px] font-medium text-red-500">
                        Cancelled
                      </span>
                    ) : (
                      <div className="shrink-0">
                        <RsvpButton
                          runId={run.id}
                          runDate={run.runDate}
                          clubId={run.club?.slug ?? run.club_id}
                          runInstanceId={run.instanceId}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function YourClubsSection({
  ledClubs,
  followedClubs,
  perClubAttendance,
  loading,
}: {
  ledClubs: LedClub[];
  followedClubs: FollowedClub[];
  perClubAttendance: Map<string, number>;
  loading: boolean;
}) {
  const showLeadingSection = ledClubs.length > 0;

  return (
    <div className="space-y-8">
      {showLeadingSection && (
        <section className="rounded-[2rem] border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-6 shadow-sm md:p-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-black">Your Clubs</h2>
              <p className="mt-1 text-sm text-black/55">Clubs you lead and manage.</p>
            </div>
            <div className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-black/60">
              {ledClubs.length} {ledClubs.length === 1 ? 'club' : 'clubs'}
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ledClubs.map((club) => {
              const attended = perClubAttendance.get(club.id) ?? 0;
              return (
                <ClubCard
                  key={club.id}
                  club={club}
                  leaderMode
                  footer={
                    <div className="space-y-3">
                      {attended > 0 && (
                        <p className="text-xs font-medium uppercase tracking-[0.12em] text-black/40">
                          {attended} {attended === 1 ? 'run' : 'runs'} attended
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/clubs/${club.slug}`}
                          className="inline-flex rounded-2xl border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-black transition hover:bg-black hover:text-white"
                        >
                          View Club
                        </Link>
                        <Link
                          to={`/clubs/${club.slug}/edit`}
                          className="inline-flex rounded-2xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
                        >
                          Edit Club
                        </Link>
                      </div>
                    </div>
                  }
                />
              );
            })}
          </div>
        </section>
      )}

      <section className="rounded-[2rem] border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-6 shadow-sm md:p-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-black">Following</h2>
            <p className="mt-1 text-sm text-black/55">Clubs you've chosen to keep up with.</p>
          </div>
          <div className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-black/60">
            {followedClubs.length} {followedClubs.length === 1 ? 'club' : 'clubs'}
          </div>
        </div>

        {loading ? (
          <div className="mt-6 text-sm text-black/55">Loading your clubs...</div>
        ) : followedClubs.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-black/10 bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] px-5 py-8 text-sm text-black/55">
            You are not following any clubs yet.
            <div className="mt-4">
              <Link
                to="/explore"
                className="inline-flex rounded-2xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
              >
                Explore Clubs
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {followedClubs.map((item) =>
              item.club ? (
                <ClubCard
                  key={item.club_id}
                  club={item.club}
                  footer={
                    <div className="space-y-1">
                      <p className="text-xs font-medium uppercase tracking-[0.12em] text-black/40">
                        Following since {formatDate(item.created_at)}
                      </p>
                      {(perClubAttendance.get(item.club_id) ?? 0) > 0 && (
                        <p className="text-xs font-medium uppercase tracking-[0.12em] text-amber-600">
                          {perClubAttendance.get(item.club_id)}{' '}
                          {perClubAttendance.get(item.club_id) === 1 ? 'run' : 'runs'} attended
                        </p>
                      )}
                    </div>
                  }
                />
              ) : null
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function ClubUpdatesFeed({
  updates,
  loading,
}: {
  updates: ClubUpdate[];
  loading: boolean;
}) {
  if (loading || updates.length === 0) return null;

  return (
    <section className="rounded-[2rem] border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] p-6 shadow-sm md:p-8">
      <h2 className="text-2xl font-semibold tracking-tight text-black">Club Updates</h2>
      <p className="mt-1 text-sm text-black/55">Recent news from your clubs.</p>

      <div className="mt-6 space-y-4">
        {updates.map((update) => (
          <div
            key={update.id}
            className="rounded-2xl border border-black/8 bg-white p-4"
          >
            <div className="flex items-center gap-2">
              {update.club?.logo && (
                <img
                  src={update.club.logo}
                  alt={update.club.name}
                  className="h-6 w-6 rounded-lg border border-black/10 object-cover"
                />
              )}
              <span className="text-xs font-semibold text-black/55">{update.club?.name}</span>
              <span className="text-xs text-black/30">·</span>
              <span className="text-xs text-black/30">{formatDate(update.created_at)}</span>
            </div>
            <h3 className="mt-2 font-semibold tracking-tight text-black">{update.title}</h3>
            <p className="mt-1 line-clamp-3 text-sm leading-relaxed text-black/60">{update.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Suggested clubs (RC-08)
// ---------------------------------------------------------------------------

type SuggestedClub = {
  id: string;
  slug: string;
  name: string;
  location: string | null;
  logo: string | null;
  hero_image: string | null;
};

function SuggestedClubsSection({
  profile,
  followedClubIds,
}: {
  profile: { goals?: string[] | null; pace_range?: string | null; neighborhood?: string | null } | null;
  followedClubIds: Set<string>;
}) {
  const [suggestions, setSuggestions] = useState<SuggestedClub[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const hasPrefs = !!(profile?.goals?.length || profile?.pace_range || profile?.neighborhood);

  useEffect(() => {
    if (!hasPrefs || fetched) return;

    async function load() {
      setLoading(true);
      // Fetch all clubs and filter client-side to match goals (vibes) via runs
      const { data: runsData } = await supabase
        .from('runs')
        .select('club_id, vibes')
        .eq('is_active', true);

      const goals = profile?.goals ?? [];
      let matchingClubIds: Set<string>;

      if (goals.length > 0) {
        matchingClubIds = new Set(
          ((runsData || []) as { club_id: string; vibes: string[] | null }[])
            .filter((r) => (r.vibes ?? []).some((v) => goals.includes(v)))
            .map((r) => r.club_id)
        );
      } else {
        matchingClubIds = new Set(
          ((runsData || []) as { club_id: string; vibes: string[] | null }[]).map((r) => r.club_id)
        );
      }

      // Remove clubs already followed
      for (const id of followedClubIds) matchingClubIds.delete(id);

      if (matchingClubIds.size === 0) {
        setSuggestions([]);
        setLoading(false);
        setFetched(true);
        return;
      }

      const { data: clubsData } = await supabase
        .from('clubs')
        .select('id, slug, name, location, logo, hero_image')
        .in('id', Array.from(matchingClubIds))
        .limit(5);

      setSuggestions((clubsData as SuggestedClub[]) || []);
      setLoading(false);
      setFetched(true);
    }

    load();
  }, [hasPrefs, fetched, profile, followedClubIds]);

  if (!hasPrefs || (!loading && suggestions.length === 0)) return null;

  return (
    <section className="rounded-[2rem] border border-black/10 bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] p-6 shadow-sm md:p-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-black">Suggested for you</h2>
          <p className="mt-1 text-sm text-black/55">Based on your running preferences.</p>
        </div>
        <Link to="/profile" className="text-xs text-black/40 transition hover:text-black">
          Edit preferences
        </Link>
      </div>

      {loading ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-3xl border border-black/10 bg-white" />
          ))}
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {suggestions.map((club) => (
            <ClubCard key={club.id} club={club} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function MyClubsPage() {
  const { user, profile, leader, loading: authLoading } = useAuth();

  const [ledClubs, setLedClubs] = useState<LedClub[]>([]);
  const [followedClubs, setFollowedClubs] = useState<FollowedClub[]>([]);
  const [updates, setUpdates] = useState<ClubUpdate[]>([]);
  const [feedLoading, setFeedLoading] = useState(true);

  const [attendanceRows, setAttendanceRows] = useState<AttendanceRow[]>([]);
  const [perClubAttendance, setPerClubAttendance] = useState<Map<string, number>>(new Map());
  const [upcomingRuns, setUpcomingRuns] = useState<UpcomingRun[]>([]);
  const [userRsvps, setUserRsvps] = useState<Map<string, 'going' | 'interested'>>(new Map());
  const [statsLoading, setStatsLoading] = useState(true);
  const [upcomingLoading, setUpcomingLoading] = useState(true);

  const allClubIds = useMemo(() => {
    const ids = new Set<string>();
    ledClubs.forEach((c) => ids.add(c.id));
    followedClubs.forEach((f) => ids.add(f.club_id));
    return ids;
  }, [ledClubs, followedClubs]);

  // Phase 1: load clubs
  useEffect(() => {
    async function loadClubs() {
      if (!user) {
        setFeedLoading(false);
        setStatsLoading(false);
        setUpcomingLoading(false);
        return;
      }

      setFeedLoading(true);

      const leaderId = leader?.id ?? null;

      const [
        { data: ledClubsData, error: ledClubsError },
        { data: followsData, error: followsError },
      ] = await Promise.all([
        leaderId
          ? supabase
              .from('club_leaders')
              .select('club:clubs (id, slug, name, location, logo, hero_image, leader_id)')
              .eq('leader_id', leaderId)
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('club_follows')
          .select(`club_id, created_at, club:clubs (id, slug, name, location, logo, hero_image)`)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
      ]);

      if (ledClubsError) console.error('Failed to load led clubs:', ledClubsError.message);
      if (followsError) {
        console.error('Failed to load followed clubs:', followsError.message);
        setFeedLoading(false);
        return;
      }

      const ledClubRows = ((ledClubsData || []) as {
        club: LedClubRow | LedClubRow[] | null;
      }[])
        .map((row) => (Array.isArray(row.club) ? row.club[0] : row.club))
        .filter((club): club is LedClubRow => Boolean(club));
      const normalizedLedClubs: LedClub[] = ledClubRows
        .map(normalizeLedClub)
        .sort((a, b) => a.name.localeCompare(b.name));
      const normalizedFollows: FollowedClub[] = ((followsData || []) as FollowedClubRow[]).map(
        (item) => ({
          club_id: item.club_id,
          created_at: item.created_at,
          club: normalizeClubRelation(item.club),
        })
      );

      setLedClubs(normalizedLedClubs);
      setFollowedClubs(normalizedFollows);
      setFeedLoading(false);
    }

    loadClubs();
  }, [user, leader]);

  // Phase 2: load stats + upcoming runs once we know which clubs the user belongs to
  useEffect(() => {
    if (!user || feedLoading) return;

    const clubIdArray = Array.from(allClubIds);
    const today = new Date();
    const isoToday = getIsoDate(today);
    const isoPlus7 = getIsoDate(new Date(today.getTime() + 7 * 86400000));

    async function loadStats() {
      setStatsLoading(true);

      const [attendanceResult, rsvpsResult] = await Promise.all([
        supabase
          .from('run_attendances')
          .select('run_id, run_date, club_id')
          .eq('user_id', user!.id)
          .order('run_date', { ascending: false })
          .limit(500),
        supabase
          .from('run_rsvps')
          .select('run_id, run_date, status')
          .eq('user_id', user!.id)
          .gte('run_date', isoToday)
          .lte('run_date', isoPlus7)
          .in('status', ['going', 'interested']),
      ]);

      const rows = (attendanceResult.data || []) as AttendanceRow[];
      setAttendanceRows(rows);

      const countMap = new Map<string, number>();
      for (const row of rows) {
        countMap.set(row.club_id, (countMap.get(row.club_id) ?? 0) + 1);
      }
      setPerClubAttendance(countMap);

      const rsvpMap = new Map<string, 'going' | 'interested'>();
      for (const r of ((rsvpsResult.data || []) as { run_id: string; run_date: string; status: 'going' | 'interested' }[])) {
        rsvpMap.set(`${r.run_id}::${r.run_date}`, r.status);
      }
      setUserRsvps(rsvpMap);

      setStatsLoading(false);
    }

    async function loadUpcomingRuns() {
      if (clubIdArray.length === 0) {
        setUpcomingRuns([]);
        setUpcomingLoading(false);
        return;
      }

      setUpcomingLoading(true);

      const days: Array<{ iso: string; dayName: string }> = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        days.push({ iso: getIsoDate(d), dayName: getDayName(d) });
      }

      const isoPlus6 = days[days.length - 1].iso;

      const [recurringResult, oneOffResult, instancesResult] = await Promise.all([
        supabase
          .from('runs')
          .select(
            'id, club_id, title, day_of_week, start_time, location_address, distance, vibes, club:clubs(id, slug, name, logo, hero_image)'
          )
          .in('club_id', clubIdArray)
          .eq('is_recurring', true)
          .eq('is_active', true),

        supabase
          .from('runs')
          .select(
            'id, club_id, title, day_of_week, start_time, location_address, distance, vibes, run_date, club:clubs(id, slug, name, logo, hero_image)'
          )
          .in('club_id', clubIdArray)
          .eq('is_recurring', false)
          .eq('is_active', true)
          .gte('run_date', isoToday)
          .lte('run_date', isoPlus6),

        supabase
          .from('run_instances')
          .select(
            'id, run_id, club_id, run_date, status, title, start_time_override, location_address_override, distance_override'
          )
          .in('club_id', clubIdArray)
          .gte('run_date', isoToday)
          .lte('run_date', isoPlus6),
      ]);

      const recurringRuns = (recurringResult.data || []) as RunRow[];
      const oneOffRuns = (oneOffResult.data || []) as RunRow[];
      const instances = (instancesResult.data || []) as RunInstanceRow[];

      // Build instance map: run_id+date → instance (last-write-wins)
      const instanceMap = new Map<string, RunInstanceRow>();
      for (const inst of instances) {
        instanceMap.set(`${inst.run_id}::${inst.run_date}`, inst);
      }

      const result: UpcomingRun[] = [];

      for (const day of days) {
        // Recurring runs matching this day
        for (const run of recurringRuns) {
          if (run.day_of_week !== day.dayName) continue;

          const inst = instanceMap.get(`${run.id}::${day.iso}`);
          if (inst?.status === 'canceled') continue;

          result.push({
            id: run.id,
            club_id: run.club_id,
            club: normalizeRunClub(run.club),
            title: inst?.title ?? run.title ?? null,
            start_time: inst?.start_time_override ?? run.start_time,
            location_address: inst?.location_address_override ?? run.location_address ?? null,
            distance: inst?.distance_override ?? run.distance ?? null,
            vibes: run.vibes ?? [],
            runDate: day.iso,
            instanceId: inst?.id ?? null,
            status: inst?.status ?? 'scheduled',
          });
        }

        // One-off runs on this exact date
        for (const run of oneOffRuns) {
          if (run.run_date !== day.iso) continue;

          const inst = instanceMap.get(`${run.id}::${day.iso}`);
          if (inst?.status === 'canceled') continue;

          result.push({
            id: run.id,
            club_id: run.club_id,
            club: normalizeRunClub(run.club),
            title: inst?.title ?? run.title ?? null,
            start_time: inst?.start_time_override ?? run.start_time,
            location_address: inst?.location_address_override ?? run.location_address ?? null,
            distance: inst?.distance_override ?? run.distance ?? null,
            vibes: run.vibes ?? [],
            runDate: day.iso,
            instanceId: inst?.id ?? null,
            status: inst?.status ?? 'scheduled',
          });
        }
      }

      result.sort((a, b) => {
        if (a.runDate !== b.runDate) return a.runDate.localeCompare(b.runDate);
        return a.start_time.localeCompare(b.start_time);
      });

      setUpcomingRuns(result);
      setUpcomingLoading(false);
    }

    async function loadUpdates() {
      const clubIdArr = Array.from(allClubIds);
      if (clubIdArr.length === 0) {
        setUpdates([]);
        return;
      }

      const { data: updatesData, error: updatesError } = await supabase
        .from('club_updates')
        .select(`id, title, body, image_url, created_at, club:clubs (id, name, logo)`)
        .in('club_id', clubIdArr)
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (updatesError) {
        console.error('Failed to load club updates:', updatesError.message);
        setUpdates([]);
      } else {
        setUpdates(
          ((updatesData || []) as ClubUpdateRow[]).map((item) => ({
            id: item.id,
            title: item.title,
            body: item.body,
            image_url: item.image_url,
            created_at: item.created_at,
            club: normalizeUpdateClubRelation(item.club),
          }))
        );
      }
    }

    // Fire all three concurrently
    Promise.all([loadStats(), loadUpcomingRuns(), loadUpdates()]);
  }, [user, feedLoading, allClubIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstName = useMemo(() => {
    if (profile?.display_name?.trim()) return profile.display_name.trim().split(' ')[0];
    if (user?.email) return user.email.split('@')[0];
    return 'Runner';
  }, [profile, user]);

  const { currentStreakWeeks, last8Weeks } = useMemo(
    () => computeWeeklyStreak(attendanceRows),
    [attendanceRows]
  );

  const totalRuns = attendanceRows.length;
  const totalClubs = allClubIds.size;

  if (authLoading) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
        <div className="mx-auto max-w-7xl px-4 pt-10 sm:px-6">
          <div className="text-sm text-black/60">Loading...</div>
        </div>
      </main>
    );
  }

  if (!user) {
    return <LoggedOutEmptyState />;
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6">
        {/* Page header */}
        <div className="mb-8 overflow-hidden rounded-[2rem] border border-black/10 bg-white shadow-[0_18px_60px_rgba(0,0,0,0.08)]">
          <div className="bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] px-8 py-10 md:px-10 md:py-12">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-black/45">
              My Clubs
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-black md:text-5xl">
              Your club world, {firstName}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-black/65 sm:text-base">
              Keep up with the run clubs you follow, track your progress, and jump back into the
              communities you care about most.
            </p>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
          {/* Sidebar */}
          <aside className="w-full space-y-4 lg:sticky lg:top-24 lg:w-72 lg:shrink-0">
            {statsLoading ? (
              <>
                <div className="h-36 animate-pulse rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe]" />
                <div className="h-28 animate-pulse rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe]" />
                <div className="h-40 animate-pulse rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe]" />
              </>
            ) : (
              <>
                <ProfileCard
                  profile={profile}
                  user={user}
                  totalRuns={totalRuns}
                  totalClubs={totalClubs}
                />
                <StreakWidget
                  last8Weeks={last8Weeks}
                  currentStreakWeeks={currentStreakWeeks}
                />
                <MilestonesPanel totalRuns={totalRuns} totalClubs={totalClubs} />
              </>
            )}
          </aside>

          {/* Main content */}
          <div className="min-w-0 flex-1 space-y-8">
            <SuggestedClubsSection
              profile={profile}
              followedClubIds={allClubIds}
            />
            <UpcomingRunsSection
              upcomingRuns={upcomingRuns}
              userRsvps={userRsvps}
              loading={upcomingLoading}
            />
            <YourClubsSection
              ledClubs={ledClubs}
              followedClubs={followedClubs}
              perClubAttendance={perClubAttendance}
              loading={feedLoading}
            />
            <ClubUpdatesFeed updates={updates} loading={feedLoading} />
          </div>
        </div>
      </div>
    </main>
  );
}
